import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { GenerateReport } from "@/api/planning";

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)} %`;
}

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

export function GenerateReportDialog({
  open,
  report,
  onClose,
}: {
  open: boolean;
  report: GenerateReport | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!report) return null;

  const ok = report.status === "OPTIMAL" || report.status === "FEASIBLE";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("planning.generationReport")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {!ok && (
            <Alert severity="error">
              <Typography>{t("planning.reportNoSolution")}</Typography>
              <Typography variant="caption">Status: {report.status}</Typography>
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              size="small"
              color={ok ? "success" : "error"}
              label={`${t("planning.reportStatus")}: ${report.status}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${t("planning.reportElapsed")}: ${fmtMs(report.elapsed_ms)}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${report.practitioners} ${t("planning.reportPractitioners").toLowerCase()}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${report.days} jours × ${report.slots} tranches`}
            />
          </Stack>

          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t("planning.reportCoverage")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {report.slots_filled} / {report.total_slots_to_fill} · {fmtPct(report.coverage_rate)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, report.coverage_rate * 100)}
              color={
                report.coverage_rate >= 0.95 ? "success" :
                report.coverage_rate >= 0.7 ? "warning" : "error"
              }
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Divider />

          <Stack direction="row" spacing={2} flexWrap="wrap">
            <StatCell label={t("planning.reportCarences")} value={report.carences} color={report.carences === 0 ? "success" : "warning"} />
            <StatCell
              label={t("planning.reportWishes")}
              value={`${report.wishes_satisfied} / ${report.wishes_total}`}
              color={report.wishes_total === 0 || report.wishes_satisfied === report.wishes_total ? "success" : "default"}
            />
            <StatCell
              label={t("planning.reportAvoids")}
              value={`${report.avoids_violated} / ${report.avoids_total}`}
              color={report.avoids_violated === 0 ? "success" : "warning"}
            />
            <StatCell
              label={t("planning.reportUnavailables")}
              value={report.unavailables_respected}
              color="success"
            />
          </Stack>

          {report.objective_value !== null && (
            <Typography variant="caption" color="text.secondary">
              Objective value: {report.objective_value.toFixed(2)} · {report.assignments_created}{" "}
              new assignments
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function StatCell({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color: "success" | "warning" | "error" | "default";
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 130 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Chip size="small" color={color} variant={color === "default" ? "outlined" : "filled"} label={value} sx={{ mt: 0.5 }} />
    </Box>
  );
}
